/**
 * ProductListPresenter
 * --
 */
import { useEffect, useState } from 'react';
import { Content, Title, Row, Col } from 'components';
import { Spin } from 'antd';
import {
  ProductSummary,
  ProductSearch,
  ProductListController,
  ProductList,
} from './components';
import { useGetProducts } from 'hooks/ProductHooks';
import moment from 'moment';
import ShopInformationList from 'routes/admin.routes/admin.orders/OrderList/components/ShopInformationList';
import styles from './style/styles';

/**----------------------------------------
 * [Component] 총관리자: 상품목록 - 프레젠터
 * ----------------------------------------
 */
const ProductListPresenter = ({
  loading,
  currentPage,
  isCheckedAll,
  checkedList,
  onPagination,
  onChecked,
  onCheckedAll,
  onDelete,
  permission,
  currentShop,
  setCurrentShop,
  setLoading,
  onToggle,
  onBulk,
  pagenationInformation,
}) => {
  /* ===== STATE ===== */
  const [searchInfo, setSearchInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [order, setOrder] = useState('createdAtDesc');
  const { data } = useGetProducts(
    {
      join_tables: '["product_category", "product_file"]',
      shop_information_id: currentShop,
    },
    {
      enabled: !!currentShop,
    },
  );
  const originProducts = data && data.status === 200 ? data.data : [];

  /* ===== Functions ===== */
  /**--------------------
   *
   * --------------------
   */
  const handleSearchProduct = (_searchInfo) => {
    setSearchInfo(_searchInfo);
  };

  /**--------------------
   *
   * --------------------
   */
  const filterSearchProduct = (products) => {
    setLoading(true);

    let filteredProducts = products;
    if (searchInfo) {
      if (searchInfo.productType) {
        filteredProducts = filteredProducts.filter((products) => {
          if (searchInfo.productType === 'product_name') {
            return products.product_name.includes(searchInfo.keyword);
          } else if (searchInfo.productType === 'product_code') {
            return products.product_code.includes(searchInfo.keyword);
          } else if (searchInfo.productType === 'product_category') {
            return products.product_category.product_category_name.includes(
              searchInfo.keyword,
            );
          }
        });
      }

      if (searchInfo.salesType) {
        filteredProducts = filteredProducts.filter((product) => {
          if (searchInfo.salesType === 'sales_wait') {
            return product.product_status === 'WAIT';
          } else if (searchInfo.salesType === 'sales') {
            return product.product_status === 'NORMAL';
          } else if (searchInfo.salesType === 'sold_out') {
            return product.product_status === 'SOLDOUT';
          } else if (searchInfo.salesType === 'sales_stop') {
            return product.product_status === 'STOP';
          } else if (searchInfo.salesType === 'sales_end') {
            return product.product_status === 'END';
          }

          return true;
        });
      }

      if (searchInfo.applicationType) {
        filteredProducts = filteredProducts.filter((product) => {
          if (searchInfo.applicationType === 'apply') {
            return product.application_status === 'APPLY';
          } else if (searchInfo.applicationType === 'reject') {
            return product.application_status === 'REJECT';
          } else if (searchInfo.applicationType === 'approve') {
            return product.application_status === 'APPROVE';
          } else if (searchInfo.applicationType === 'wait') {
            return product.application_status === 'WAIT';
          }

          return true;
        });
      }

      if (searchInfo.type) {
        filteredProducts = filteredProducts.filter((product) => {
          if (searchInfo.type === 'delivery') {
            return product.type === 'DELIVERY';
          } else if (searchInfo.type === 'rental') {
            return product.type === 'RENTAL';
          } else if (searchInfo.type === 'subscription') {
            return product.type === 'SUBSCRIPTION';
          }

          return true;
        });
      }

      if (Array.isArray(searchInfo.dates)) {
        filteredProducts = filteredProducts.filter((product) => {
          const target = moment.unix(product.created_at);
          const startDate = moment(
            searchInfo.dates[0]
              ? searchInfo.dates[0]
              : moment().subtract(1, 'year'),
            'YYYYMMDD',
          );
          const endDate = moment(
            searchInfo.dates[1] ? searchInfo.dates[1] : moment().endOf('day'),
            'YYYYMMDD',
          );
          if (!searchInfo.dates[0]) {
            return target < endDate;
          }
          return target.isBetween(startDate, endDate, 'day', '[]');
        });
      }

      if (Array.isArray(searchInfo.updates)) {
        filteredProducts = filteredProducts.filter((product) => {
          const target = moment.unix(product.updated_at);
          const startDate = moment(
            searchInfo.updates[0]
              ? searchInfo.updates[0]
              : moment().subtract(1, 'year'),
            'YYYYMMDD',
          );
          const endDate = moment(
            searchInfo.updates[1]
              ? searchInfo.updates[1]
              : moment().endOf('day'),
            'YYYYMMDD',
          );
          if (!searchInfo.updates[0]) {
            return target < endDate;
          }
          return target.isBetween(startDate, endDate, 'day', '[]');
        });
      }

      if (searchInfo.exhibition) {
        filteredProducts = filteredProducts.filter((product) => {
          if (searchInfo.exhibition === 'best') {
            return product.is_best === 'Y';
          } else if (searchInfo.exhibition === 'new') {
            return product.is_new === 'Y';
          } else if (searchInfo.exhibition === 'brand') {
            return product.is_show_brand === 'Y';
          }

          return true;
        });
      }
    }

    return filteredProducts;
  };

  /**--------------------
   *
   * --------------------
   */
  const sortProducts = (products, order) => {
    const sortedProducts = [...products];

    switch (order) {
      case 'createdAtDesc': // 등록일순 (최신순)
        return sortedProducts.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
      case 'createdAtAsc': // 등록일 역순 (오래된순)
        return sortedProducts.sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at),
        );
      case 'priceDesc': // 높은 가격순
        return sortedProducts.sort((a, b) => b.price - a.price);
      case 'priceAsc': // 낮은 가격순
        return sortedProducts.sort((a, b) => a.price - b.price);
      default:
        return sortedProducts; // 복사본 반환
    }
  };

  /**--------------------
   *
   * --------------------
   */
  const handleChangeOrder = (order) => {
    setOrder(order);
  };

  /* ===== Hooks ===== */
  /**--------------------
   *
   * --------------------
   */
  useEffect(() => {
    let _products =
      data && data.status === 200 ? filterSearchProduct(data.data) : [];
    _products = sortProducts(_products, order);
    // const filterdata = data?.data?.filter((item) => {
    //   return item.product_name.includes('프리모');
    // });
    // console.log(filterdata);
    setTimeout(() => {
      setLoading(false);
      setProducts(_products);
    }, 1000);
  }, [data, searchInfo, order]);
  /* ===== Render ===== */
  // JSX: start
  return (
    <Spin spinning={loading}>
      <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
        <Row>
          <Col x={24}>
            <Title size={3} style={{ marginBottom: 10 }}>
              상품 목록
            </Title>

            {/* === 검색필터: start === */}
            <ProductSearch onSearch={handleSearchProduct} />
            {/* === 검색필터: end === */}

            {/* === 요약: start === */}
            <ProductSummary products={originProducts} />
            {/* === 요약: end === */}

            {/* === Container: start === */}
            <Row gutter={[1, 1]} className="boxShadow">
              {/* === 업체목록 - start === */}
              {permission === 'ADMIN' && (
                <Col x={4}>
                  <ShopInformationList
                    defaultId={currentShop}
                    onChange={setCurrentShop}
                  />
                </Col>
              )}
              {/* === 업체목록 - end === */}
              <Col x={permission === 'ADMIN' ? 20 : 24}>
                {/* === 검색결과: start === */}
                <Content
                  maxWidth={'100%'}
                  width={1400}
                  height={'100%'}
                  minHeight={490}
                  borderRadius={4}
                  padding={20}
                >
                  {/* <Content maxWidth={'100%'} borderRadius={4} padding={20}> */}
                  {/* 상단 컨트롤러 버튼 */}
                  <ProductListController
                    products={products}
                    productCount={products.length}
                    onDelete={onDelete}
                    onChangeOrder={handleChangeOrder}
                    onBulk={onBulk}
                    permission={permission}
                    checkedList={checkedList}
                  />
                  {/* 상품목록 */}
                  <ProductList
                    products={products}
                    currentPage={currentPage}
                    isCheckedAll={isCheckedAll}
                    checkedList={checkedList}
                    onPagination={onPagination}
                    onChecked={onChecked}
                    onCheckedAll={onCheckedAll}
                    onToggle={(i, k, v) =>
                      onToggle(i, k, v, products, setProducts)
                    }
                    pagenationInformation={pagenationInformation}
                    currentShop={currentShop}
                  />
                </Content>
                {/* === 검색결과: end === */}
              </Col>
            </Row>
            {/* === Container: end === */}
          </Col>
        </Row>
      </Content>
    </Spin>
  ); // JSX: end
};

export default ProductListPresenter;
